import React from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHeader } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { Home, DoorOpen, Droplets, Plug, TriangleAlert, MessageSquare } from "lucide-react";

const LOOK_FOR = [
  { icon: Home, title: "Overall structure", points: ["Cracks in walls, ceilings, or foundation", "Sagging roofline or rooflines that don't match", "Sloping or uneven floors"] },
  { icon: Droplets, title: "Water & moisture", points: ["Water stains on ceilings or under sinks", "Musty smells in basements or closets", "Signs of past flooding or sump pumps"] },
  { icon: Plug, title: "Systems & utilities", points: ["Age of HVAC, water heater, and roof", "Enough electrical outlets; panel condition", "Water pressure — test a few faucets"] },
  { icon: DoorOpen, title: "Day-to-day livability", points: ["Natural light at the time of day you'd be home", "Storage and closet space", "Noise from roads, neighbors, or trains"] },
];

const RED_FLAGS = [
  "Fresh paint only in one small area (can hide stains or damage)",
  "Doors or windows that stick or won't close",
  "Strong air fresheners masking smells",
  "DIY electrical or plumbing work that looks unpermitted",
  "Standing water or poor drainage around the foundation",
];

const QUESTIONS = [
  "How old are the roof, HVAC, and water heater?",
  "Have there been any major repairs or insurance claims?",
  "What's included in the sale (appliances, fixtures)?",
  "Why are the owners selling, and how long has it been listed?",
  "Are there any known issues disclosed by the seller?",
];

export default function TouringPrep() {
  return (
    <div>
      <PageHeader title="Touring prep hub" subtitle="A calm checklist for before and during a home tour. Note what you see — your inspector and agent help interpret it later." />

      <div className="grid md:grid-cols-2 gap-6">
        {LOOK_FOR.map((s, i) => (
          <motion.div key={s.title} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}>
            <Card className="p-6 rounded-2xl h-full" data-testid={`touring-section-${i}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-xl bg-forest/10 text-forest flex items-center justify-center"><s.icon className="h-5 w-5" /></div>
                <h3 className="font-heading font-semibold text-lg">{s.title}</h3>
              </div>
              <ul className="space-y-2">
                {s.points.map((p) => (
                  <li key={p} className="text-sm text-muted-foreground flex gap-2"><span className="text-primary mt-1">•</span>{p}</li>
                ))}
              </ul>
            </Card>
          </motion.div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Card className="p-6 rounded-2xl" data-testid="touring-redflags">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-status-warning/15 text-status-warning flex items-center justify-center"><TriangleAlert className="h-5 w-5" /></div>
            <h3 className="font-heading font-semibold text-lg">Red flags to note</h3>
          </div>
          <ul className="space-y-2">
            {RED_FLAGS.map((r) => <li key={r} className="text-sm text-muted-foreground flex gap-2"><span className="text-status-warning mt-1">•</span>{r}</li>)}
          </ul>
        </Card>
        <Card className="p-6 rounded-2xl" data-testid="touring-questions">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><MessageSquare className="h-5 w-5" /></div>
            <h3 className="font-heading font-semibold text-lg">Questions to ask</h3>
          </div>
          <Accordion type="single" collapsible className="w-full">
            {QUESTIONS.map((q, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-sm text-left">{q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">Ask your agent to help you get a clear answer and note it for later comparison.</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      </div>

      <div className="mt-6">
        <Disclaimer>This guide helps you observe, not diagnose. Always hire your own licensed home inspector before making decisions. Look Before You Leap 808 does not assess property condition or value.</Disclaimer>
      </div>
    </div>
  );
}
